import type { ResolvedDependency } from '@wix/ambassador-app-service-webapp/types';
import type { MarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import type { GetAppResponse } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { HttpClient } from '@wix/http-client';
import { getAppDependencies } from './appDependencies';
import {
  fetchAppsNames,
  getAllAppMarketListings,
  getAppsInfoByLanguageWithDefaults,
} from './appMarketListings';
import { fetchAppInfo } from './devCenterApps';
import { isAppUpdateAvailable } from './isAppUpdateAvailable';
import { DocumentServicesObject } from '@wix/document-services-types';
import { GetAppMarketListingParams } from '@wix/editor-platform-host-integration-apis';
import { isGuid } from '../guid';

class AppMarketService {
  constructor(private httpClient: HttpClient) {}

  public getAllAppMarketListings(
    params: GetAppMarketListingParams,
  ): Promise<MarketListing[]> {
    return getAllAppMarketListings(this.httpClient, params);
  }

  public getAppsInfoByLanguageWithDefaults(
    params: GetAppMarketListingParams,
  ): Promise<Record<string, MarketListing>> {
    return getAppsInfoByLanguageWithDefaults(
      this.httpClient,
      params.appDefinitionIds,
      params.languageCode || 'en',
    );
  }

  public fetchAppsNames(
    appDefIds: string[],
    languageCode?: string,
  ): Promise<Record<string, string>> {
    return fetchAppsNames(this.httpClient, appDefIds, languageCode);
  }

  public getAppDependencies(
    appId: string,
    appVersion?: string,
  ): Promise<ResolvedDependency[]> {
    return getAppDependencies(appId, appVersion);
  }

  public fetchAppInfo(
    appId: string,
    version: string = 'latest',
  ): Promise<GetAppResponse | undefined> {
    return fetchAppInfo(this.httpClient, appId, version);
  }

  public isAppUpdateAvailable(
    documentServices: DocumentServicesObject,
    appDefId: string,
    latestVersion?: string,
  ): boolean {
    return isAppUpdateAvailable(documentServices, appDefId, latestVersion);
  }

  public triggerAppMarketDataFetchForInitialApps(
    documentServicesAPI: DocumentServicesObject,
  ): void {
    try {
      const installedApps =
        documentServicesAPI.platform.getInstalledApps?.() || [];

      if (installedApps.length === 0) {
        return;
      }

      installedApps
        .filter(
          (appData) =>
            appData.appDefinitionId && isGuid(appData.appDefinitionId),
        )
        .forEach((appData) => {
          try {
            documentServicesAPI.tpa.appMarket.getData(appData.appDefinitionId!);
          } catch (error) {
            console.warn(
              `Failed to fetch app market data for initially loaded app ${appData.appDefinitionId}:`,
              error,
            );
          }
        });
    } catch (error) {
      console.warn(
        'Failed to trigger app market data fetch for initially loaded apps:',
        error,
      );
    }
  }
}

const appMarketService = new AppMarketService(new HttpClient());

export { AppMarketService, appMarketService };
